import { useState } from 'react';

import { useTranslation } from 'react-i18next';

import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { DEFAULT_TOOL_SETTINGS, type ToolSettings } from '@/shared/types/toolSettings';

import ToolDetailsTab from './ToolDetailsTab';
import ToolTestTab, { type CallToolMutation } from './ToolTestTab';
import ToolVoiceTab from './ToolVoiceTab';
import type { McpServer, McpTool } from '../hooks/useMcpServers';
import { sortKeys } from '../ResyncDialog';

const TAB_DETAILS = 'details';
const TAB_VOICE = 'voice';
const TAB_TEST = 'test';

// `TabsContent` is force-mounted so the Test form keeps its state across tab switches. Radix
// hides an inactive panel with the `hidden` *attribute*, which a Tailwind `flex` class would
// override, hence the explicit `data-[state=inactive]:hidden`. `mt-0` cancels the default `mt-2`.
const tabContentClasses = 'mt-0 flex min-h-0 flex-1 flex-col gap-4 data-[state=inactive]:hidden';

type ToolOverviewPanelProps = {
  tool: McpTool | null;
  server: McpServer;
  canWrite: boolean;
  emptyMessage: string;
  isLoading?: boolean;
  callToolMutation: CallToolMutation;
  onChangeSettings: (name: string, settings: ToolSettings) => void;
};

const ToolOverviewPanel = ({
  tool,
  server,
  canWrite,
  emptyMessage,
  isLoading,
  callToolMutation,
  onChangeSettings,
}: ToolOverviewPanelProps) => {
  const { t } = useTranslation();
  const [tab, setTab] = useState<string>(TAB_DETAILS);

  return (
    <Tabs
      value={tab}
      onValueChange={setTab}
      className="flex h-full min-w-0 flex-1 flex-col gap-4 px-4 py-6"
    >
      <div className="flex shrink-0 items-center gap-4 pb-4">
        <h2 className="min-w-0 flex-1 truncate text-lg leading-none font-semibold tracking-tight text-foreground">
          {t('mcp.tool_overview')}
        </h2>
        {/* `min-w-14` evens out the triggers: "Test" is far shorter than "Details" in every
            locale, and without a floor the active pill visibly changes width on switch. Set here
            rather than on the shared `default` variant, which ~11 other tab bars use — some of
            them, like the environment switcher, deliberately space-constrained. */}
        <TabsList className="border border-stroke">
          <TabsTrigger value={TAB_DETAILS} className="min-w-14">
            {t('common.details')}
          </TabsTrigger>
          <TabsTrigger value={TAB_VOICE} className="min-w-14">
            {t('mcp.voice')}
          </TabsTrigger>
          <TabsTrigger value={TAB_TEST} className="min-w-14">
            {t('common.test')}
          </TabsTrigger>
        </TabsList>
      </div>

      {isLoading && (
        <div className="flex flex-col gap-4 [&>*]:bg-background">
          <Skeleton className="h-5 w-24" />
          <Skeleton className="h-5 w-40" />
          <Skeleton className="h-5 w-full" />
          <Skeleton className="h-5 w-2/3" />
        </div>
      )}

      {!isLoading && !tool && (
        <p className="text-sm leading-5 text-foreground-muted">{emptyMessage}</p>
      )}

      {tool && (
        <>
          <TabsContent value={TAB_DETAILS} forceMount className={tabContentClasses}>
            <ToolDetailsTab tool={tool} buildDate={server.verified_at ?? server.updated} />
          </TabsContent>
          <TabsContent value={TAB_VOICE} className={tabContentClasses}>
            <ToolVoiceTab
              tool={tool}
              settings={{ ...DEFAULT_TOOL_SETTINGS, ...server.tool_settings?.[tool.name] }}
              canWrite={canWrite}
              onChangeSettings={(settings) => onChangeSettings(tool.name, settings)}
            />
          </TabsContent>
          <TabsContent value={TAB_TEST} forceMount className={tabContentClasses}>
            {/* Keyed by tool *and* schema: form values are positional, so they must survive
                neither a change of tool nor a change of the same tool's arguments. The live
                tools/list lands after the form captured its defaults, and on a `needs_sync`
                server it brings a different argument set — reusing the values would submit
                them under whichever argument now sits at that position.

                Key ordering is normalised first: the stored and the live copy of an unchanged
                schema routinely serialise in a different order, and remounting on that alone
                would throw away whatever the user had already typed. */}
            <ToolTestTab
              key={`${tool.name}:${JSON.stringify(sortKeys(tool.inputSchema ?? {}))}`}
              tool={tool}
              server={server}
              canWrite={canWrite}
              isActive={tab === TAB_TEST}
              callToolMutation={callToolMutation}
            />
          </TabsContent>
        </>
      )}
    </Tabs>
  );
};

export default ToolOverviewPanel;
